import { useEffect, useRef, useState } from 'react';
import { SxProps, Tooltip, Typography, TypographyProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { theme } from '@store';

const truncateStyleWithWidth = (width?: string) => ({
  ...(width
    ? { maxWidth: width }
    : {
        [theme.breakpoints.up('lg')]: { maxWidth: '20rem' },
        [theme.breakpoints.down('lg')]: { maxWidth: '10rem' },
        [theme.breakpoints.down('md')]: { maxWidth: '7rem' },
        [theme.breakpoints.down('sm')]: { maxWidth: '5rem' },
      }),
  webkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

interface ITruncatedTextWithConditionalTooltipProps {
  text?: string | number | null | boolean;
  width?: string;
  sx?: SxProps<Theme>;
  placement?: 'top' | 'bottom';
  variant?:
    | 'body1'
    | 'body2'
    | 'subtitle1'
    | 'subtitle2'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'caption';
  color?: TypographyProps['color'];
}

export const TruncatedTextWithConditionalTooltip = ({
  text,
  variant = 'body1',
  sx = {},
  width,
  placement = 'top',
  color,
}: ITruncatedTextWithConditionalTooltipProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, [text]);

  return (
    <Tooltip
      title={isOverflowing ? text : ''}
      placement={placement}
      arrow
      slotProps={{
        popper: {
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        },
      }}
    >
      <Typography
        ref={textRef}
        variant={variant}
        component={'span'}
        sx={{
          display: 'inline-block',
          ...truncateStyleWithWidth(width),
          ...sx,
        }}
        {...(color && { color: color })}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};
